import './cellRenderers.css';
import { useEffect, useReducer, useRef } from 'react';
import { getGroupOrdinal, getGroupRowNodes } from './groupSelectionUtils';

/**
 * 自定义的“合并多行选择”checkbox 列的单元格渲染。
 *
 * 数据按分组字段连续排列后，配合列的 spanRows，同一组的多行会合并成一个单元格，
 * checkbox 在合并后的单元格里垂直居中。勾选会同时选中/取消选中该组的所有行。
 * ag-grid 的 cell spanning 只会渲染合并区域的第一行（锚点行），被合并覆盖的行不渲染该列单元格，
 * 因此这里拿到的 node 一定是组内第一行。
 *
 * 注意：该列依赖表格开启了 rowSelection（多选），否则 api.setNodesSelected 不会生效。
 */
function GroupSelectionCellRenderer(params) {
  const { api, node, colDef, eGridCell } = params;
  const inputRef = useRef(null);
  // ag-grid 的 React 组件不会在 selectionChanged / modelUpdated 时自动重渲染，
  // 这里订阅事件手动触发重渲染，保证 checkbox 的勾选状态能正确反映出来。
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  // ag-grid 的 cellRenderer params 没有顶层 field，字段在 colDef.field 上
  const field = colDef?.field;
  // 是否合并空值数据。默认（notMergeEmptyValue 未配置或为 true）空值各自独立，
  // 仅当显式配置为 false 时空值之间才会合并成一组。
  const mergeEmpty = colDef?.notMergeEmptyValue === false;

  useEffect(() => {
    const refresh = () => forceUpdate();
    api.addEventListener('selectionChanged', refresh);
    api.addEventListener('modelUpdated', refresh);
    return () => {
      if (!api.isDestroyed()) {
        api.removeEventListener('selectionChanged', refresh);
        api.removeEventListener('modelUpdated', refresh);
      }
    };
  }, [api]);

  const groupNodes = getGroupRowNodes(api, node, field, mergeEmpty);
  const selectableNodes = groupNodes.filter((n) => n.data?.selectable !== false);
  const selectedCount = selectableNodes.filter((n) => n.isSelected()).length;

  const checked = selectableNodes.length > 0 && selectedCount === selectableNodes.length;
  const indeterminate = selectedCount > 0 && selectedCount < selectableNodes.length;
  const disabled = selectableNodes.length === 0;

  // indeterminate 只能通过 dom property 设置，无法通过 html attribute
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !checked && indeterminate;
    }
  }, [checked, indeterminate]);

  // 合并单元格不会自动跟随 .ag-row-selected 变色，这里根据组内是否有选中手动给单元格加 class，
  // 让合并的 checkbox cell 选中时的背景色和普通单行 checkbox cell 保持一致。
  const cellSelected = selectedCount > 0;
  useEffect(() => {
    if (eGridCell) {
      eGridCell.classList.toggle('icp-group-select-cell-selected', cellSelected);
    }
  }, [eGridCell, cellSelected]);

  // 给相邻的组打上奇偶 class，方便用户自行配置不同的样式（如不同颜色的边框）做组间区分。
  // 如需针对某张表格的这一列单独定制样式，直接在 columnDef 上配置 cellClass 即可。
  const isEvenGroup = getGroupOrdinal(api, node, field, mergeEmpty) % 2 === 0;
  useEffect(() => {
    if (eGridCell) {
      eGridCell.classList.toggle('icp-group-select-even', isEvenGroup);
      eGridCell.classList.toggle('icp-group-select-odd', !isEvenGroup);
    }
  }, [eGridCell, isEvenGroup]);

  const handleChange = () => {
    if (api.isDestroyed() || !selectableNodes.length) {
      return;
    }
    api.setNodesSelected({
      nodes: selectableNodes,
      newValue: !checked,
      source: 'checkboxSelected',
    });
  };

  return (
    <label className="icp-group-selection-checkbox" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
      />
    </label>
  );
}

export default GroupSelectionCellRenderer;
